import * as React from "react";
import { styled } from "@mui/material/styles";
import { isEqual } from "lodash";
import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Typography,
  Checkbox,
} from "@mui/material";
import { AppBody } from "../../AppBody";
import Breadcrumbs from "../../../containers/Breadcrumbs";
import {
  appHeaderTitle,
  fileNameFormat,
  reduceObj,
} from "../../../common/services/Utilities";
import BranchOfficeSearchForm from "../../../containers/BranchOfficeSearchForm";
import {
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../interfaces";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import useSortProperties from "../../../hooks/useSortProperties";
import SearchPills from "../../../containers/SearchPills";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import {
  buildFilterString,
  buildSearchString,
  fetchData,
} from "../../../common/services/ApiClient";
import BackButton from "../../../containers/BackButton";
import SodInstitutionDetails from "../SodInstitutionDetails";
import SODStateList from "./SODStateList";
import BHCList from "./BHCList";
import { ApiBaseUrl } from "../../../config";
import StateByInstType from "./StateByInstType";
import {
  getStateName,
  selectedStatesAlpha,
} from "../../../refdata/FilterOptions";
import DepositsNameSearchTable from "../../../containers/DepositsNameSearchTable";
import BackToResultsButton from "../../../containers/BackToResultsButton";
import SelectedMarketTable from "../../../containers/SelectedMarketTable";
import CloudFoundryBanner from "../../../containers/CloudFoundryBanner";
import ShowHideButton from "../../ShowHideButton";
import LoadingIndicator from "../../LoadingIndicator";
import USAMap from "../../../containers/BODUSAMap";
import ResultsViewToggle from "../../FusionCharts/ResultsViewToggle";
import { useNavigate, useLocation } from "react-router-dom";
import TableChartIcon from "@mui/icons-material/TableChart";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import generateBranchOfficeDepositsApiUrl from "./BranchOfficeDepositsUtilities";

const PREFIX = "BranchOfficeDeposits";

const classes = {
  rightAligned: `${PREFIX}-rightAligned`,
  modalFooter: `${PREFIX}-modalFooter`,
  footerText: `${PREFIX}-footerText`,
  summaryDepositsTextBox: `${PREFIX}-summaryDepositsTextBox`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.rightAligned}`]: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.modalFooter}`]: {
    display: "flex",
    position: "sticky",
    bottom: 0,
    paddingBottom: 8,
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },

  [`& .${classes.footerText}`]: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },

  [`& .${classes.summaryDepositsTextBox}`]: {
    position: "relative",
    color: "white",
    background: theme.palette.primary.main,
  },
}));

interface Props {
  appState: Query;
  updateSort: (sortField: string, sortDirection: string) => void;
  updateFilter: (field: string, value: string | string[]) => void;
  updateUi: (field: string, value: KeyValue[] | boolean | KeyValue) => void;
  providedAppState?: Query;
  hideToggle?: boolean;
}

interface DropDownOptions {
  label: string;
  value: any;
}

export default function BranchOfficeDeposits({
  appState,
  updateSort,
  updateFilter,
  updateUi,
  providedAppState,
  hideToggle,
}: Props) {
  const isCountyView = Boolean(appState.filters.stalpbr !== "");
  const pgcStep1 = appState?.ui?.pgcStep === "step1";
  const newProvidedAppState = providedAppState
    ? { ...providedAppState }
    : undefined;
  if (newProvidedAppState && pgcStep1) {
    newProvidedAppState.ui = { ...newProvidedAppState.ui, pgcStep: "step1" };
  }
  const depositAppState = newProvidedAppState || appState;

  const sortProperties = useSortProperties(depositAppState, updateSort);

  const [openCloseHeader, setOpenCloseHeader] = React.useState<KeyValue>({});
  const [openClose, setOpenClose] = React.useState("");
  const [bhcName, setBhcName] = React.useState("");
  const [showMap, setShowMap] = React.useState(false);
  const [bhcRSSD, setBhcRSSD] = React.useState(depositAppState.filters.bhcRSSD);
  const [cert, setCert] = React.useState(depositAppState.filters.cert);
  const [previousSearch, setPreviousSearch] = React.useState<KeyValue>({});
  const [stateData, setStateData] = React.useState<KeyValue[]>([]);
  const [dataView, setDataView] = React.useState<"table" | "map">("table");
  const [checked, setChecked] = React.useState<KeyValue>(
    cert && appState.ui?.partialMergerCheckboxes?.hasOwnProperty(cert)
      ? appState.ui.partialMergerCheckboxes[cert]
      : {},
  );
  const [report, setReport] = React.useState<any>("all-subsidiary");
  const [dropDownOptions, setDropDownOptions] = React.useState<
    DropDownOptions[]
  >([
    {
      label: "All Subsidiary Banks and Thrifts",
      value: "all-subsidiary",
    },
  ]);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [displayBHCTable, setDisplayBHCTable] = React.useState(true);
  const [mapInitialMode, setMapInitialMode] = React.useState<"map" | "table">(
    "map",
  );
  const navigate = useNavigate();
  const location = useLocation();
  const isBHC = depositAppState.ui.institutionType === "bhcs";

  const indeterminateState =
    Object.keys(checked).length > 0 &&
    Object.keys(checked).some(
      (checkbox: string) => checked[checkbox].checked,
    ) &&
    Object.keys(checked).some((checkbox: string) => !checked[checkbox].checked);

  const allCheckedState = initialLoad
    ? depositAppState.ui.partialMerger?.[0]?.isChecked
    : Object.keys(checked).length > 0 &&
      Object.keys(checked).every(
        (checkbox: string) => checked[checkbox].checked,
      );

  const handleCheckboxChange = () => {
    const checkboxes = Object.keys(checked).map((state) => {
      return {
        key: state,
        value: indeterminateState
          ? {
              checked: depositAppState.ui.partialMerger?.[0]?.isChecked,
              count: checked[state].count,
              deposits: checked[state].deposits,
              rssdhcr: checked[state]?.rssdhcr,
            }
          : {
              checked: !allCheckedState,
              count: checked[state].count,
              deposits: checked[state].deposits,
              rssdhcr: checked[Object.keys(checked)[0]]?.rssdhcr,
            },
      };
    });
    setChecked(reduceObj(checkboxes));
  };
  let checkboxTotalCount = 0;
  let checkboxSumCount = 0;
  if (Object.keys(checked).length > 0) {
    const topLevelKeys = Object.keys(checked).filter(
      (key) => !key.includes("_"),
    );
    const topLevelKeyCounts = topLevelKeys.map((key) =>
      checked[key].checked ? checked[key].count : 0,
    );
    if (topLevelKeyCounts.every((val) => val > 0)) {
      topLevelKeys.forEach((val) => {
        checkboxTotalCount += checked[val].count;
        checkboxSumCount += checked[val].deposits;
      });
    } else {
      Object.keys(checked).forEach((val) => {
        if (checked[val].checked) {
          checkboxTotalCount += checked[val].count;
          checkboxSumCount += checked[val].deposits;
        }
      });
    }
  }

  const continueBtn = (
    <Button
      id={"continue_btn"}
      sx={(theme) => ({
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.secondary.main,
        width: "165px",
        height: "32px",
        fontSize: "14px",
      })}
      onClick={() => {
        const checkboxCounts = {
          count: checkboxTotalCount,
          depsum: checkboxSumCount,
          cert: depositAppState.filters.cert,
          rssdhcr: checked[Object.keys(checked)[0]]?.rssdhcr,
        };
        const hasMatchingCert = depositAppState.ui.partialMerger.some(
          (obj: KeyValue) =>
            obj.hasOwnProperty("cert") &&
            obj.cert === depositAppState.filters.cert,
        );
        if (!hasMatchingCert) {
          const uniqueItems = (
            newData: KeyValue[],
            currentData: KeyValue[],
          ): KeyValue[] => {
            const updatedData = [...currentData];
            newData.forEach((newItem: KeyValue) => {
              const isPresent = currentData.some((item: KeyValue) =>
                isEqual(item, newItem),
              );
              if (!isPresent) {
                updatedData.push(newItem);
              }
            });
            return updatedData;
          };
          const merger: KeyValue[] = depositAppState.ui.partialMerger;
          const newItems: KeyValue[] = uniqueItems([checkboxCounts], merger);
          const updatedData: KeyValue[] = [
            ...depositAppState.ui.partialMerger,
            ...newItems,
          ].filter((obj: KeyValue) => obj.hasOwnProperty("cert"));
          let uniqueArray = Array.from(new Set(updatedData));
          if (uniqueArray && uniqueArray.length > 0) {
            updateUi("partialMerger", uniqueArray);
            const updatedObj = {
              ...appState.ui.partialMergerCheckboxes,
              [cert]: checked,
            };
            updateUi("partialMergerCheckboxes", updatedObj);
          }
        } else {
          const partialMergers: KeyValue[] = depositAppState.ui.partialMerger;
          partialMergers.forEach((obj: KeyValue) => {
            if (obj.cert === depositAppState.filters.cert) {
              obj.depsum = checkboxCounts.depsum;
              obj.count = checkboxCounts.count;
            }
          });
          updateUi("PartialMerger", partialMergers);
          const updatedObj = {
            ...appState.ui.partialMergerCheckboxes,
            [cert]: checked,
          };
          updateUi("partialMergerCheckboxes", updatedObj);
        }
        updateUi("closeModal", true);
      }}
    >
      {"Continue"}
    </Button>
  );
  const cancelBtn = (
    <Button
      id={"cancel_btn"}
      sx={(theme) => ({
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.secondary.main,
        width: "165px",
        height: "32px",
        fontSize: "14px",
      })}
      onClick={() => {
        updateUi("closeModal", true);
      }}
    >
      {"Cancel"}
    </Button>
  );

  const introParagraph = (
    <>
      {appHeaderTitle("branchOffice")}
      <Typography variant="body2" id="peer-group-description">
        This Branch Office Locator allows you to get a listing of branch
        offices, and their reported deposits (as of June 30 per year), for both
        institutions and holding companies. You can search using the parent
        institution or holding company’s name or ID number, and include
        geographic location for highly targeted searches. Or you can search with
        geographic criteria only to reveal matching institutions to investigate
        in-depth. Ultimately, the listings will provide a specific institution’s
        branch office data by state, county, city, ZIP, and MSA.
      </Typography>
      <Typography variant="body2" id="peer-group-feedback-link">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=SOD"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );
  const isStateReport = ["stateCharterClass", "stateCounty"].includes(
    providedAppState?.ui.reportType,
  );
  const openCloseButton =
    openCloseHeader.ALL && Object.keys(openCloseHeader.ALL).length > 1 ? (
      <Button
        id={
          Object.values(openCloseHeader.ALL).every((val) => val)
            ? "closeAllStates"
            : "openAllStates"
        }
        sx={(theme) => ({
          "minWidth": 80,
          "margin": 0,
          "color": "#ffffff",
          "fontWeight": 600,
          "backgroundColor": "#426cac",
          "&:hover, &.Mui-focusVisible": {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.secondary.main,
          },
        })}
        onClick={() => {
          setOpenClose("ALL");
        }}
      >
        {Object.values(openCloseHeader.ALL).every((val) => val)
          ? "Close All"
          : "Open All"}
      </Button>
    ) : (
      ""
    );
  const certHeaderMetaData: KeyValue[] = [
    {
      title: "State / County / Street Address",
      apiField: "",
      styles: { textAlign: "left", width: 200 },
    },
    {
      title: openCloseButton,
      apiField: "",
      styles: { maxWidth: 100 },
    },
    ...(depositAppState.ui?.partialMerger &&
    depositAppState.ui.partialMerger.length > 0 &&
    depositAppState.filters.cert !== depositAppState.ui.certAcquiring
      ? [
          {
            title: (
              <>
                Select:
                <Checkbox
                  id={"all_checkbox"}
                  checked={allCheckedState}
                  inputProps={{ "aria-label": "checkbox" }}
                  sx={{
                    "color": "#fff",
                    "&.Mui-checked": { color: "#fff" },
                    "&:hover": { backgroundColor: "#ffffff", color: "#1A4480" },
                  }}
                  onChange={handleCheckboxChange}
                  indeterminate={indeterminateState}
                />
              </>
            ),
            apiField: "",
            styles: { width: 60 },
          },
        ]
      : []),
    { title: "City (USPS)", apiField: "", styles: { width: 120 } },
    { title: "Zip", apiField: "", styles: { width: 60 } },
    {
      title: "Matching Offices",
      apiField: "",
      styles: { min: 60 },
    },
    {
      title: "Unique Number",
      apiField: "",
      styles: { minWidth: 60, maxWidth: 80 },
    },
    { title: "Office Number", apiField: "", styles: { minWidth: 60 } },
    { title: "Service Type", apiField: "", styles: { width: 120 } },
    {
      title: pgcStep1 ? "Established" : "Office Code",
      apiField: "",
      styles: { minWidth: 60 },
    },
    {
      title: pgcStep1 ? "Acquired" : "Matching Deposits (Thousands USD)",
      apiField: "",
      styles: pgcStep1
        ? { minWidth: 60, textAlign: "center" }
        : { textAlign: "right", width: 140 },
    },
  ].filter(Boolean);

  const bhcsHeaderMetaData: KeyValue[] = [
    {
      title: "Institution Name / State / County / Branch Name",
      apiField: "",
      styles: { textAlign: "left", width: 350, minWidth: 350 },
    },
    {
      title: openCloseButton,
      apiField: "",
      styles: { minWidth: 116, width: 116 },
    },
    {
      title: "Main Office / Branch Address",
      apiField: "",
      styles: { width: 200, minWidth: 200 },
    },
    { title: "City (USPS)", apiField: "", styles: {} },
    { title: "Zip", apiField: "", styles: { minWidth: 52, width: 52 } },
    {
      title: "Total Offices",
      apiField: "",
      styles: { textAlign: "left" },
    },
    { title: "Cert / UNINUMBR", apiField: "", styles: {} },
    { title: "Class", apiField: "", styles: {} },
    { title: "Office Number", apiField: "", styles: {} },
    { title: "Service Type", apiField: "", styles: { minWidth: 100 } },
    { title: "Office Code", apiField: "", styles: {} },
    {
      title: "Total Deposits (Thousands USD)",
      apiField: "",
      styles: { textAlign: "right", minWidth: 150 },
    },
  ];

  const locationOnlyMetaData: KeyValue[] = [
    {
      title: isBHC
        ? "State / County / Bank Holding Company Name"
        : "State / County / Institution Name",
      apiField: "",
      styles: {
        textAlign: "left",
        width: isBHC ? 310 : 219,
        minWidth: isBHC ? 310 : 219,
      },
    },
    {
      title: openCloseButton,
      apiField: "",
      styles: { minWidth: 116, width: 116 },
    },
    { title: "Main Office City, State", apiField: "", styles: {} },
    {
      title: isBHC ? "Unique BHCs*" : "Unique Institutions*",
      apiField: "",
      styles: { minWidth: 52, width: 52 },
    },
    {
      title: "Matching Offices",
      apiField: "",
      styles: { width: 52, minWidth: 52 },
    },
    { title: isBHC ? "RSSDHCR" : "CERT", apiField: "", styles: {} },
    ...(!isBHC ? [{ title: "Class", apiField: "", styles: {} }] : []),
    {
      title: "Matching Deposits (Thousands USD)",
      apiField: "",
      styles: { textAlign: "right", width: 140, minWidth: 140 },
    },
  ];
  const certTableHeaders: TableHeader[][] = [
    certHeaderMetaData.map((element) => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];

  const bhcTableHeaders: TableHeader[][] = [
    bhcsHeaderMetaData.map((element) => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];

  const locationOnlyTableHeaders: TableHeader[][] = [
    locationOnlyMetaData.map((element) => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];

  const branchOfficeSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${depositAppState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
    {
      label: "Cert",
      value: depositAppState.filters.cert
        ? depositAppState.filters.cert.toString()
        : "",
      search: false,
      static:
        appState.ui.summaryTableLevelTwo ||
        appState.ui.reportType === "top50CommercialBanks" ||
        appState.ui.reportType === "marketPresence" ||
        !!providedAppState,
      formKey: "cert",
    },
    {
      label: "Name",
      value: depositAppState.search.name,
      search: true,
      formKey: "name",
    },
    {
      label: "BHC Name",
      value: depositAppState.search.bhcName,
      search: true,
      formKey: "bhcName",
    },
    {
      label: "State",
      value: depositAppState.filters.stname
        ? depositAppState.filters.stname
        : isCountyView
          ? `${appState.filters.stalpbr}`
          : `${
              selectedStatesAlpha.find(
                (state: KeyValue) =>
                  state.value === depositAppState.filters.stalpbr,
              )?.label ?? ""
            }`,
      search: false,
      formKey: isCountyView ? "stalpbr" : "stname",
      static:
        (depositAppState.ui.summaryTableLevelTwo ||
          !!providedAppState ||
          isCountyView) &&
        !(isCountyView && !isStateReport),
    },
    {
      label: "County",
      value: depositAppState.filters.county,
      search: false,
      formKey: "county",
      static: !!providedAppState,
    },
    {
      label: "ZIP",
      value: depositAppState.filters.zipbr,
      search: false,
      formKey: "zipbr",
    },
    {
      label: "MSA",
      value: depositAppState.filters.msa,
      search: false,
      formKey: "msa",
      static: !!providedAppState,
    },
    {
      label: "DOCKET",
      value: depositAppState.filters.OTSDocket,
      search: false,
      formKey: "OTSDocket",
    },
    {
      label: "RSSDID",
      value: depositAppState.filters.rssdid,
      search: false,
      formKey: "rssdid",
    },
    {
      label: "BHC RSSDHCR",
      value: depositAppState.filters.bhcRSSD
        ? depositAppState.filters.bhcRSSD.toString()
        : "",
      search: false,
      static:
        depositAppState.ui.reportType === "top50BHC" ||
        appState.ui.reportType === "marketPresence" ||
        !!providedAppState,
      formKey: "bhcRSSD",
    },
  ];

  const downloadHeaders =
    depositAppState.ui.institutionType === "banks"
      ? [
          { label: "Cert", key: "CERT" },
          { label: "Report Period", key: "YEAR" },
          { label: "Institution Name", key: "NAMEFULL" },
          { label: "State", key: "STNAMEBR" },
          { label: "County", key: "CNTYNAMB" },
          { label: "Street Address", key: "ADDRESBR" },
          { label: "City (USPS)", key: "CITY2BR" },
          { label: "Zip", key: "ZIPBR" },
          { label: "Unique Number", key: "UNINUMBR" },
          { label: "Office Number", key: "BRNUM" },
          { label: "Service Type", key: "BRSERTYPDESC" },
          { label: "Office Code", key: "BRCENM" },
          {
            label: "Total Deposits (Thousands USD)",
            key: "DEPSUMBR",
          },
        ]
      : [
          {
            label: "BHC Name",
            key: "NAMEHCR",
          },
          {
            label: "Branch Name",
            key: "NAMEBR",
          },
          {
            label: "State",
            key: "STNAMEBR",
          },
          {
            label: "County",
            key: "CNTYNAMB",
          },
          {
            label: "Institution Name",
            key: "NAMEFULL",
          },
          {
            label: "RSSDHCR",
            key: "RSSDHCR",
          },
          { label: "Main Office / Branch Office", key: "ADDRESBR" },
          { label: "City (USPS)", key: "CITY2BR" },
          { label: "Zip", key: "ZIPBR" },
          { label: "Total Offices", key: "ADDRESBR" },
          { label: "Cert", key: "CERT" },
          { label: "Unique Number", key: "UNINUMBR" },
          { label: "Class", key: "BKCLASS" },
          { label: "Office Number", key: "BRNUM" },
          { label: "Service Type", key: "BRSERTYPDESC" },
          { label: "Office Code", key: "BRCENM" },
          {
            label: "Total Deposits (Thousands USD)",
            key: "DEPSUMBR",
          },
        ];

  const fileNameRaw = fileNameFormat(
    `Summary_Of_Deposits${
      depositAppState.filters.msa ? `_MSA_${depositAppState.filters.msa}` : ""
    }_raw`,
  );
  const fileNameFormatted = fileNameFormat(
    `Summary_Of_Deposits${
      depositAppState.filters.msa ? `_MSA_${depositAppState.filters.msa}` : ""
    }_formatted`,
  );

  const selectedMarkets: string[][] = [];

  if (
    appState.filters.stateNames?.[0] &&
    !appState.filters.msa &&
    !appState.filters.zipbr
  ) {
    for (let i = 0; i < appState.filters.stateNames.length; i++) {
      const market = [
        getStateName(selectedStatesAlpha, appState.filters.stateNames[i]),
        appState.ui.marketSelection[i]?.counties?.join(", "),
        appState.ui.marketSelection[i]?.cities?.join(", "),
        appState.ui.marketSelection[i]?.zips?.join(", "),
      ];
      selectedMarkets.push(market);
    }
  }

  const apiData: apiDownloadProps[] = [
    {
      appState: depositAppState,
      fileName: fileNameRaw,
      apiPath: "sod",
      text: "Download Results Data - raw",
    },
    {
      appState: depositAppState,
      fileName: fileNameFormatted,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      headers: downloadHeaders,
      additionalContent:
        appState.filters?.stateNames?.[0] &&
        !appState.filters.msa &&
        !appState.filters.zipbr
          ? [
              ["Selected Market"],
              ["State", "Counties", "Cities", "ZIP Codes"],
              ...selectedMarkets,
            ]
          : undefined,
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={branchOfficeSearchPills} />}
    />
  );
  const certKey = appState.filters.cert || providedAppState?.filters.cert;
  const bhcKey = providedAppState?.filters.bhcRSSD;

  const marketSelectionUninums =
    depositAppState.ui.marketSelection &&
    depositAppState.ui.marketSelection
      .map((market: KeyValue) => {
        return (isBHC ? bhcKey : certKey) &&
          market.hasOwnProperty("certUninums")
          ? market.certUninums[isBHC ? bhcKey : certKey]
          : undefined;
      })
      .filter((val: number) => val)
      .flat();

  const marketSelectUninumFilters = marketSelectionUninums?.[0]
    ? `AND UNINUMBR:(${marketSelectionUninums.join(" OR ")})`
    : "";
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (
      Object.keys(depositAppState.ui.partialMerger?.[0] || {}).length > 0 &&
      initialLoad
    ) {
      setInitialLoad(false);
    }
  }, [depositAppState.ui.partialMerger, initialLoad]);
  React.useEffect(() => {
    if (depositAppState.filters.stname || depositAppState.filters.state) {
      if (
        depositAppState.search.bhcName ||
        Object.keys(depositAppState.ui.marketSelection?.[0]).length > 0 ||
        depositAppState.filters.zipbr ||
        depositAppState.filters.msa ||
        depositAppState.filters.bhcRSSD !== bhcRSSD ||
        depositAppState.filters.cert !== cert
      ) {
        depositAppState.filters.stname && updateFilter("stname", "");
        depositAppState.filters.state && updateFilter("state", "");
        updateFilter("county", "");
        depositAppState.filters.bhcRSSD !== bhcRSSD &&
          setBhcRSSD(depositAppState.filters.bhcRSSD);
        depositAppState.filters.cert !== cert &&
          setCert(depositAppState.filters.cert);
      }
    }
    if (
      !(
        depositAppState.filters.stname ||
        depositAppState.filters.state ||
        depositAppState.filters.stalpbr
      ) &&
      depositAppState.filters.county
    ) {
      updateFilter("county", "");
    }
  }, [updateFilter, depositAppState, bhcRSSD, cert]);
  React.useEffect(() => {
    let isMounted = true;
    setLoading(true);

    if (
      depositAppState.filters.bhcRSSD &&
      depositAppState.filters.bhcRSSD !== 0
    ) {
      const searchType = "RSSDHCR";
      const searchValue = depositAppState.filters.bhcRSSD;

      (async () => {
        try {
          const response = await fetchData(
            `${ApiBaseUrl()}/sod?filters=${searchType}:"${searchValue}" AND YEAR:${depositAppState.filters.lastYear}&limit:1`,
          );
          setDisplayBHCTable(
            response.data[0] && depositAppState.filters.bhcRSSD !== "0000000",
          );
          setBhcName(response.data[0]?.NAMEHCR);
        } catch (error: any) {
          console.error(`${error.name} : ${error.message}`);
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      })();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [depositAppState.filters.bhcRSSD, depositAppState.filters.lastYear]);

  React.useEffect(() => {
    if (depositAppState?.filters?.bhcRSSD && dropDownOptions.length < 2) {
      const fetchUrls = [
        fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(
            depositAppState,
            undefined,
            undefined,
            undefined,
            "CERT",
          ),
        ),
        fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(depositAppState, {
            by: "CERT",
            terms: [""],
            sum: ["DEPSUMBR"],
          }),
        ),
      ];
      let isMounted = true;
      (async () => {
        await Promise.all(fetchUrls)
          .then((response) => {
            if (isMounted) {
              const data = response[0].data;
              const subsidiaries: any = Array.from(
                new Map(
                  (data ?? []).map((item: any) => [
                    item.NAMEFULL,
                    {
                      label: `CERT ${item.CERT} - ${item.NAMEFULL}`,
                      value: item.CERT,
                    },
                  ]),
                ).values(),
              );
              setDropDownOptions([
                {
                  label: "All Subsidiary Banks and Thrifts",
                  value: "all-subsidiary",
                },
                ...subsidiaries,
              ]);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [depositAppState, dropDownOptions.length]);

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      ui: appState.ui.institutionTypes,
    };
    if (
      (appState.filters.msa ||
        appState.filters.zipbr ||
        appState.filters.stateNames?.[0] ||
        appState.filters.cert ||
        cert ||
        appState.filters.OTSDocket ||
        appState.filters.rssdid) &&
      (Object.keys(appState.ui.marketSelection?.[0] || {}).length === 0 ||
        appState.filters.stateNames[0] === "ALL") &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setPreviousSearch(filtersSearch);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(
            cert
              ? {
                  ...appState,
                  filters: {
                    ...appState.filters,
                    cert,
                  },
                }
              : appState,
            {
              by: "STALPBR",
              limit: 1000,
              sum: ["DEPSUMBR"],
            },
            undefined,
            undefined,
            undefined,
            isBHC ? "RSSDHCR:>0" : undefined,
          ),
        )
          .then((response) => {
            if (isMounted) {
              setStateData(response.data);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [appState, cert, isBHC, previousSearch]);
  const hasMP = stateData.some((item: KeyValue) => item.STALPBR === "MP");
  const prevUrl = sessionStorage.getItem("prevUrl");

  const container = (children: React.ReactNode) => {
    return providedAppState ? (
      <>{children}</>
    ) : (
      <>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Breadcrumbs currentPageBreadCrumbTitle="Branch Office Deposits" />
          </Grid>
        </Grid>
        <CloudFoundryBanner />
        <AppBody
          introText={introParagraph}
          searchForm={
            <BranchOfficeSearchForm
              disableAdvancedSearch={isCountyView}
              isCountyView={isCountyView}
            />
          }
          backButton={
            <BackButton
              app="SOD"
              buttonLabel={
                prevUrl?.includes("bankfind")
                  ? "Back to Institution Details"
                  : "Back to SOD"
              }
              variant="dark"
            />
          }
        >
          {children}
        </AppBody>
      </>
    );
  };
  const cancelAndContinueBtns = (
    <>
      <div className={classes.modalFooter}>
        <div>
          <Typography className={classes.footerText}>
            Branch Offices Selected: {checkboxTotalCount}
          </Typography>
          <Typography className={classes.footerText}>
            Total Deposits Selected:{" $"}
            {checkboxSumCount?.toLocaleString()}
          </Typography>
        </div>
        <div className={classes.rightAligned}>
          <div style={{ padding: 8 }}>{cancelBtn}</div>
          <div style={{ padding: 8 }}>{continueBtn}</div>
        </div>
      </div>
    </>
  );

  return container(
    <Root>
      {loading ? (
        <LoadingIndicator
          text="Loading"
          spinnerSize={32}
          textSx={{
            fontSize: 20,
            width: "100%",
            textAlign: "center",
          }}
          spinnerSx={{
            marginTop: 24,
            width: "100%",
            textAlign: "center",
          }}
          table
        />
      ) : (depositAppState.filters.cert ||
          depositAppState.filters.OTSDocket ||
          depositAppState.filters.rssdid) &&
        depositAppState.ui.institutionType === "banks" ? (
        <>
          {!isCountyView && (
            <>
              <BackToResultsButton />
              <SodInstitutionDetails
                appState={depositAppState}
                modal={!!providedAppState}
              />
            </>
          )}
          {appState.ui.reportType !== "proForma" && searchPills}
          {isCountyView && !providedAppState && (
            <ResultsViewToggle
              mode="table"
              options={[
                { label: "table", icon: <TableChartIcon /> },
                { label: "map", icon: <PlaceOutlinedIcon /> },
              ]}
              onModeChange={(newMode) => {
                if (newMode === "map") {
                  setMapInitialMode("map");
                  updateFilter("cert", "");
                  updateFilter("rssdid", "");
                  updateFilter("county", "");
                  navigate(
                    {
                      pathname: location.pathname,
                      search: "",
                    },
                    {
                      state: location.state,
                    },
                  );
                }
                if (newMode === "table") {
                  updateFilter("county", "");
                }
              }}
            />
          )}
          {!isCountyView && providedAppState && !hideToggle && (
            <ResultsViewToggle
              mode={dataView}
              options={[
                { label: "table", icon: <TableChartIcon /> },
                { label: "map", icon: <PlaceOutlinedIcon /> },
              ]}
              onModeChange={(newMode) => {
                if (newMode === "map") {
                  setDataView("map");
                  setMapInitialMode("map");
                  setShowMap(true);
                }
                if (newMode === "table") {
                  setDataView("table");
                  setShowMap(false);
                }
              }}
            />
          )}
          {isCountyView && providedAppState && (
            <ResultsViewToggle
              mode={dataView}
              options={[
                { label: "table", icon: <TableChartIcon /> },
                { label: "map", icon: <PlaceOutlinedIcon /> },
              ]}
              onModeChange={(newMode) => {
                if (newMode === "map") {
                  setDataView("map");
                  setMapInitialMode("map");
                  setShowMap(true);
                }
                if (newMode === "table") {
                  setDataView("table");
                  setShowMap(false);
                }
              }}
              onBackToNational={() => {
                updateFilter("stalpbr", "");
                updateFilter("county", "");
              }}
              backToNational={!isStateReport && dataView === "map"}
            />
          )}
          {appState.ui.reportType !== "proForma" && <SelectedMarketTable />}
          <Box
            id="searchResults"
            sx={{
              color: "primary.main",
            }}
          >
            <Box sx={{ display: "flex" }}>
              <Typography
                component="div"
                sx={{
                  height: 40,
                  marginLeft: "auto",
                }}
              >
                <ApiDataDownload
                  app="SOD"
                  apiData={apiData}
                  post={{
                    filters: `${buildFilterString(
                      depositAppState.filters,
                    )}${marketSelectUninumFilters}`,
                    search: buildSearchString(depositAppState.search),
                    sort_order: "ASC",
                    sort_by: "STNAMEBR,CNTYNAMB",
                  }}
                  apiPathLink="sod"
                  commaSeparatedNumbers={["DEPSUMBR"]}
                  bodTotals
                />
              </Typography>
            </Box>
            <Divider />
          </Box>
          {showMap ? (
            <USAMap
              providedAppState={providedAppState}
              initialMode={mapInitialMode}
              modalHeatMap
              depositAppState={depositAppState}
              reportType={appState.ui.reportType}
              cert={cert}
            />
          ) : (
            <>
              <MultiHeaderTable
                useStickyHeader
                sortProperties={sortProperties}
                tableBody={
                  <SODStateList
                    appState={depositAppState}
                    openClose={openClose}
                    cert={cert}
                    setOpenClose={setOpenClose}
                    openCloseAllDisplay={openCloseHeader}
                    setOpenCloseAllDisplay={setOpenCloseHeader}
                    setChecked={setChecked}
                    checked={checked}
                  />
                }
                tableHeaders={certTableHeaders}
              />
              {(depositAppState.filters?.stateNames?.includes("MP") ||
                depositAppState.filters?.stalpbr === "MP" ||
                hasMP) && (
                <Typography variant="body2" color="primary.main">
                  **AKA Commonwealth of the Northern Mariana Islands
                </Typography>
              )}
            </>
          )}
          {Object.keys(depositAppState.ui.partialMerger?.[0] || {}).length >
            0 &&
            depositAppState.ui?.certAcquiring !==
              depositAppState.filters.cert &&
            cancelAndContinueBtns}
        </>
      ) : depositAppState.filters.bhcRSSD && isBHC ? (
        <>
          {!providedAppState && <BackToResultsButton />}
          <SodInstitutionDetails
            appState={depositAppState}
            modal={!!providedAppState}
          />
          <SelectedMarketTable />
          {searchPills}
          {providedAppState && (
            <ResultsViewToggle
              mode={dataView}
              options={[
                { label: "table", icon: <TableChartIcon /> },
                { label: "map", icon: <PlaceOutlinedIcon /> },
              ]}
              {...(dataView === "map" && {
                dropDownOptions: dropDownOptions,
                dropDown: report,
                setDropDown: (selected: string) => {
                  setReport(selected as string | number);
                },
              })}
              onModeChange={(newMode) => {
                if (newMode === "map") {
                  setDataView("map");
                  setMapInitialMode("map");
                  setShowMap(true);
                }
                if (newMode === "table") {
                  setDataView("table");
                  setShowMap(false);
                }
              }}
              onBackToNational={() => {
                updateFilter("stalpbr", "");
                updateFilter("county", "");
              }}
              backToNational={
                isCountyView && !isStateReport && dataView === "map"
              }
            />
          )}
          {showMap ? (
            <USAMap
              providedAppState={providedAppState}
              initialMode={mapInitialMode}
              modalHeatMap
              depositAppState={depositAppState}
              reportType={appState.ui.reportType}
              cert={cert || (report !== "all-subsidiary" ? report : undefined)}
            />
          ) : (
            displayBHCTable && (
              <>
                <Box
                  color="primary.main"
                  id="searchResults"
                  sx={{ display: "flex" }}
                >
                  <Typography
                    component="div"
                    style={{
                      height: 40,
                      marginLeft: "auto",
                    }}
                  >
                    <ApiDataDownload
                      app="SOD"
                      apiData={apiData}
                      post={{
                        filters: `${buildFilterString(
                          depositAppState.filters,
                        )}${marketSelectUninumFilters}`,
                        search: buildSearchString(depositAppState.search),
                        sort_order: "ASC",
                        sort_by: "STNAMEBR,CNTYNAMB",
                      }}
                      apiPathLink="sod"
                      commaSeparatedNumbers={["DEPSUMBR"]}
                      bodTotals
                    />
                  </Typography>
                </Box>
                <Divider />
                <Typography
                  variant="h4"
                  color="primary"
                  style={{ marginTop: "2rem" }}
                >
                  Bank and Thrift Subsidiaries Held by {bhcName}
                </Typography>
                <MultiHeaderTable
                  useStickyHeader
                  sortProperties={sortProperties}
                  tableBody={
                    <BHCList
                      appState={depositAppState}
                      openClose={openClose}
                      setOpenClose={setOpenClose}
                      openCloseAllDisplay={openCloseHeader}
                      setOpenCloseAllDisplay={setOpenCloseHeader}
                    />
                  }
                  tableHeaders={bhcTableHeaders}
                />
                <StateByInstType appState={depositAppState} />
              </>
            )
          )}
        </>
      ) : depositAppState.search.name || depositAppState.search.bhcName ? (
        <>
          <SelectedMarketTable />
          <DepositsNameSearchTable searchPills={searchPills} />
        </>
      ) : depositAppState.filters.msa ||
        depositAppState.filters.zipbr ||
        depositAppState.filters.stateNames?.[0] ? (
        <>
          {searchPills}
          <SelectedMarketTable />

          <MultiHeaderTable
            useStickyHeader
            sortProperties={sortProperties}
            tableBody={
              <SODStateList
                appState={depositAppState}
                openClose={openClose}
                setOpenClose={setOpenClose}
                cert={cert}
                openCloseAllDisplay={openCloseHeader}
                setOpenCloseAllDisplay={setOpenCloseHeader}
                locationOnlySearch
                checked={checked}
                setChecked={setChecked}
              />
            }
            tableHeaders={locationOnlyTableHeaders}
          />
          <Typography variant="body2" color="primary.main">
            *All totals in this column represent the count of{" "}
            <span style={{ fontWeight: "bold" }}>
              {isBHC ? `unique BHC entities` : `Institutions`}
            </span>{" "}
            that are present at each level. The sum of rows should not be
            considered as a grand total amount due to the potential of
            duplicating previously counted{" "}
            {isBHC ? `BHC entities` : `Institutions`}.
          </Typography>
          {(depositAppState.filters?.stateNames?.includes("MP") ||
            depositAppState.filters?.stalpbr === "MP" ||
            hasMP) && (
            <Typography variant="body2" color="primary.main">
              **AKA Commonwealth of the Northern Mariana Islands
            </Typography>
          )}
        </>
      ) : (
        <USAMap initialMode={mapInitialMode} />
      )}
    </Root>,
  );
}
